import React from 'react';
import PropTypes from 'prop-types';
import { Container, Typography, Card, Button, Grid } from 'components/base';
import i18next from 'internationalization';

import useEditorContext from '../context/editorContext';
import {
  getOriginalPage,
  isChangePageEnabled,
  isRemovePageEnabled,
  isDuplicatePageEnabled,
  getOtherOrientationPage,
  getPossiblePagesVariants,
  getOtherPageOfGroup,
  isSelectInitialPageEnabled,
  getPagesFilterVariants,
  getTransformedPage,
  getBleedZone,
} from 'editor-utils/template';
import Page from '../components/page/preview';
import useEditorRouter from '../lib/editorRouter';
import DesignSelect from '../components/designSelect';
import generateClassicData from '../generator/generateClassicData';
import { generateUpdatedPage } from '../generator/utils';
import PageWrapper from '../components/page/wrapper';
import SwitchPanel from '../components/switchPanel';
import SelectPanel from '../components/selectPanel';
import PhotoQualityAlert, { usePhotosQuality } from '../components/photoQualityAlert';
import usePhotoWellContext from '../context/photoWellContext';
import usePhotoCropperContext from '../context/photoCropperContext';
import pagesValidator from '../lib/pagesValidator';
import { formatData } from 'editor-utils/dataParser';

const DefaultEditorPreview = props => {
  const editorContext = useEditorContext();
  const photoWell = usePhotoWellContext();
  const photoCropper = usePhotoCropperContext();

  const editorRouter = useEditorRouter(props.product.id);

  const gridColProps = {
    xs: props.config.numberOfPages.xs ? 12 / props.config.numberOfPages.xs : undefined,
    sm: props.config.numberOfPages.sm ? 12 / props.config.numberOfPages.sm : undefined,
    md: props.config.numberOfPages.md ? 12 / props.config.numberOfPages.md : undefined,
    lg: props.config.numberOfPages.lg ? 12 / props.config.numberOfPages.lg : undefined,
    xl: props.config.numberOfPages.xl ? 12 / props.config.numberOfPages.xl : undefined,
  };

  const { photos, pages } = props.item;

  const [photosQuality, handleOnPhotosQuality, acceptableQuality] = usePhotosQuality(
    props.template.template.minDPI,
  );

  const handleOnAddToCartClick = e => {
    e.preventDefault();

    const validator = pagesValidator(props.template, props.item.pages, props.config);
    const data = formatData(props.template, props.item, { validator });

    if (validator.isValid()) {
      props.onFinish(data);
      return;
    }

    const errors = validator.getErrors();
    if (errors.length > 0) {
      // TODO show all warnings
      editorContext.toast(errors[0].text, {
        type: 'warning',
      });
    }
  };

  const handleOnChangePages = pageId => {
    const newPage = generateUpdatedPage(props.template, pages[0], pageId);
    editorContext.changePage(props.item.id, newPage, { pageIndex: 0 });
  };

  let onRemovePage;
  if (isRemovePageEnabled(props.template, pages)) {
    onRemovePage = (e, index) => {
      e.preventDefault();

      editorContext.removePage(props.item.id, index);
    };
  }

  let onDuplicatePage;
  if (isDuplicatePageEnabled(props.template, pages)) {
    onDuplicatePage = (e, index) => {
      e.preventDefault();

      editorContext.duplicatePage(props.item.id, index);
    };
  }

  const onChangePageOrientation = (e, index, pageId) => {
    e.preventDefault();

    const newPage = generateUpdatedPage(props.template, pages[index], pageId);
    editorContext.changePage(props.item.id, newPage, { pageIndex: index });
  };

  const handleOnChangePagesGroup = (type, checked) => {
    //
    // TODO
    // Type and checked value defines how to transform pages
    //
    editorContext.setPages(
      props.item.id,
      pages.map(p => ({
        ...p,
        id: getOtherPageOfGroup(props.template, p).id,
        // TODO reset photos
      })),
    );
  };

  const handleOnChangePagesFilter = (type, value) => {
    editorContext.setPages(
      props.item.id,
      pages.map(p => ({
        ...p,
        filter: value ? type : undefined,
      })),
    );
  };

  const handleOnChangeInitialPage = async initialPageId => {
    const { data, error } = await generateClassicData(
      editorContext.accessToken,
      props.template,
      photos,
      initialPageId,
    );
    if (!error) {
      editorContext.setPages(props.item.id, data.pages);
    }
  };

  const handleOnPagePhotoUpload = (pageIndex, placeholder) => {
    photoWell.show({
      uploadEnabled: true,
      onSelectPhotos: selectedPhotos => {
        editorContext.addPhotoToPlaceholder(
          props.item.id,
          pageIndex,
          placeholder.id,
          selectedPhotos[0],
        );
        photoWell.hide();
      },
    });
  };

  const handleOnPagePhotoEdit = (pageIndex, placeholder, photo) => {
    photoCropper.show({
      placeholder,
      photo,
      onSuccess: data => {
        editorContext.setPhotoData(props.item.id, pageIndex, placeholder.id, data);
      },
    });
  };

  const handleOnPageTextChange = (pageIndex, placeholder, value, override) => {
    editorContext.setTextData(props.item.id, pageIndex, placeholder.id, value, override);
  };

  const necessaryToReplacePhotos = props.config.withQualityCheck && !acceptableQuality;

  return (
    <Container>
      <PhotoQualityAlert
        visible={props.config.withQualityCheck}
        photosQuality={photosQuality}
        minDPI={props.template.template.minDPI}
        bestDPI={props.template.template.bestDPI}
        className="mb-3"
      />

      <Card className="text-center mx-auto mw-480 mb-3">
        <Card.Body>
          <Typography.H4 className="text-warning">PREVIEW YOUR CREATION</Typography.H4>
          <Typography.P className="mb-1">
            {i18next.t(
              `product_preview_message.${props.product.id}`,
              'Preview your print below. To rotate, crop, or replace a photo, just tap the photo. Click the three dots on a print to change the orientation, duplicate or delete. Click Start Again to upload more photos.',
            )}
            <br />
            Note: if you click Start Again, your edits will be reverted.
          </Typography.P>
          <Grid.Row>
            <Grid.Col sm={6}>
              <Button
                block
                type={necessaryToReplacePhotos ? 'success' : 'default'}
                onClick={() => {
                  editorRouter.push();
                }}
              >
                Start Again
                {/* {necessaryToReplacePhotos ? 'Use Another Photo' : 'Upload More Photos'} */}
              </Button>
            </Grid.Col>
            <Grid.Col sm={6}>
              <Button
                block
                type="success"
                disabled={necessaryToReplacePhotos}
                onClick={handleOnAddToCartClick}
              >
                Add to Cart
              </Button>
            </Grid.Col>
          </Grid.Row>
        </Card.Body>
      </Card>

      {getPossiblePagesVariants(props.template, pages, ({ type, checked, title, description }) => (
        <SwitchPanel
          key={type}
          title={title}
          checked={checked}
          description={description}
          type={type}
          onChange={value => handleOnChangePagesGroup(type, value)}
        />
      ))}

      {getPagesFilterVariants(props.template, pages, ({ type, checked, title, description }) => (
        <SwitchPanel
          key={type}
          title={title}
          checked={checked}
          description={description}
          type={type}
          onChange={value => handleOnChangePagesFilter(type, value)}
        />
      ))}

      {isChangePageEnabled(props.template, pages) && (
        <DesignSelect
          title="PICK A DESIGN"
          active={pages[0].id}
          onChange={handleOnChangePages}
          items={props.template.pages.map(page => ({
            key: page.id,
            category: page.caption,
            preview: (
              <Page
                page={getTransformedPage(props.template, pages[0], page.id)}
                template={props.template}
                photos={photos}
                preview
              />
            ),
          }))}
          className="mb-3"
        />
      )}

      {isSelectInitialPageEnabled(props.template, initialPages => (
        <SelectPanel
          active={pages[0].id}
          title="OUR CALENDAR IS RATHER SPECIAL"
          description="You can choose when you want it to start. It doesn’t have to be January!"
          items={initialPages.map(p => ({ id: p.id, label: p.label }))}
          onChange={handleOnChangeInitialPage}
        />
      ))}

      <Grid.Row className={props.config.rowClassName}>
        {pages.map((page, index) => {
          const originalPage = getOriginalPage(props.template, page);
          const otherOrientationPage = getOtherOrientationPage(props.template, page);

          return (
            <Grid.Col key={page.key} {...gridColProps} className="col-page-preview mb-1">
              <PageWrapper
                page={originalPage}
                fullWidth={props.config.fullColWidth}
                onDuplicate={onDuplicatePage ? e => onDuplicatePage(e, index) : undefined}
                onRemove={onRemovePage ? e => onRemovePage(e, index) : undefined}
                onChangeOrientation={
                  otherOrientationPage
                    ? e => onChangePageOrientation(e, index, otherOrientationPage.id)
                    : undefined
                }
              >
                <Page
                  page={getOriginalPage(props.template, page)}
                  photos={photos}
                  template={props.template}
                  withQualityCheck={props.config.withQualityCheck}
                  fullWidth={props.config.fullColWidth}
                  onPhotoUpload={(...data) => handleOnPagePhotoUpload(index, ...data)}
                  onPhotoEdit={(...data) => handleOnPagePhotoEdit(index, ...data)}
                  onTextChange={(...data) => handleOnPageTextChange(index, ...data)}
                  onPhotosQuality={goodQuality =>
                    handleOnPhotosQuality(`page-${index}`, goodQuality)
                  }
                  interactive
                  bleed={getBleedZone(props.template, page)}
                />
              </PageWrapper>
            </Grid.Col>
          );
        })}
      </Grid.Row>
    </Container>
  );
};

DefaultEditorPreview.propTypes = {
  item: PropTypes.object,
  params: PropTypes.arrayOf(PropTypes.string),
  config: PropTypes.object,
  product: PropTypes.object,
  template: PropTypes.object,
  onFinish: PropTypes.func,
};

export default DefaultEditorPreview;
